import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'data/store';
import { FeatureFlag } from 'data/models/featureFlag';
import { checkFeatureFlag } from 'data/actions/featureFlag';
import { selectHouseholdMetadata } from 'data/selectors/householdSelectors';

// Returns [enabled, loaded] — `loaded` is false until the flag API call resolves.
// Use this when rendering must be deferred until the flag is confirmed, to avoid
// briefly treating qvault2=true as false during the async loading window.
export const useFeatureFlagLoaded = (flag?: FeatureFlag, iaCode?: string): [boolean, boolean] => {
  const dispatch = useAppDispatch();
  const [state, setState] = useState(() =>
    flag === undefined ? { enabled: true, loaded: true } : { enabled: false, loaded: false },
  );

  useEffect(() => {
    let active = true;

    const getFeatureFlag = async (flag: FeatureFlag) => {
      try {
        const response = await dispatch(checkFeatureFlag(flag, iaCode));
        if (active) {
          setState({ enabled: response.featureEnabled, loaded: true });
        }
      } catch {
        if (active) {
          setState({ enabled: false, loaded: true });
        }
      }
    };

    if (flag === undefined) {
      setState({ enabled: true, loaded: true });
    } else {
      setState({ enabled: false, loaded: false });
      getFeatureFlag(flag);
    }

    return () => { active = false; };
  }, [flag, iaCode, dispatch]);

  return [state.enabled, state.loaded];
};

export const useFeatureFlag = (flag?: FeatureFlag, iaCode?: string): boolean =>
  useFeatureFlagLoaded(flag, iaCode)[0];

export const useQvault2Flag = (): [boolean, boolean] => {
  const { iaCode } = useSelector(selectHouseholdMetadata);
  return useFeatureFlagLoaded('qvault2', iaCode);
};

export const useQvault2MigrationFlag = (): [boolean, boolean] => {
  const { iaCode } = useSelector(selectHouseholdMetadata);
  return useFeatureFlagLoaded('qvault2Migration', iaCode);
};

export const useQconn2Flag = (): [boolean, boolean] => {
  const { iaCode } = useSelector(selectHouseholdMetadata);
  return useFeatureFlagLoaded('qconn2', iaCode);
};
